import { useState, useMemo } from "react";
import {
  
  ChevronLeft,
  ChevronRight,
  Add as Plus,
  AccessTime as Clock,
  LocationOn as MapPin,
  Group as Users,
  Videocam as Video,
  
  Delete as Trash2,
  Close as X,
  Save,
} from "@mui/icons-material";
import Layout from "../components/layout/Calayout";

const TaskCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [tasks, setTasks] = useState([
    {
      _id: "1",
      title: "Client Meeting",
      date: new Date(),
      time: "10:00",
      status: "pending",
      priority: "high",
      client: "John Doe",
      location: "Conference Room A",
      isVirtual: false,
      attendees: ["Alice", "Bob"],
      notes: "Quarterly review meeting",
    },
    {
      _id: "2",
      title: "Project Review",
      date: new Date(Date.now() + 86400000),
      time: "14:30",
      status: "in-progress",
      priority: "medium",
      client: "Tech Corp",
      location: "",
      isVirtual: true,
      attendees: ["Team Lead", "Developer"],
      notes: "Sprint planning session",
    },
    {
      _id: "3",
      title: "Design Workshop",
      date: new Date(Date.now() + 172800000),
      time: "09:00",
      status: "completed",
      priority: "low",
      client: "Creative Agency",
      location: "Studio B",
      isVirtual: false,
      attendees: ["Designer", "UX Lead"],
      notes: "UI/UX design session",
    },
  ]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [newTask, setNewTask] = useState({
    title: "",
    date: new Date().toISOString().split("T")[0],
    time: "09:00",
    status: "pending",
    priority: "medium",
    client: "",
    location: "",
    isVirtual: false,
    attendees: "",
    notes: "",
  });

  const today = new Date();

  const handlePreviousMonth = () => {
    setCurrentDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };

  const handleNextMonth = () => {
    setCurrentDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };

  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const days = [];

    // Previous month days
    const prevMonth = new Date(year, month, 0);
    const daysInPrevMonth = prevMonth.getDate();
    for (let i = firstDayOfMonth - 1; i >= 0; i--) {
      days.push({
        day: daysInPrevMonth - i,
        date: new Date(year, month - 1, daysInPrevMonth - i),
        isCurrentMonth: false,
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        date: new Date(year, month, i),
        isCurrentMonth: true,
      });
    }

    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        day: i,
        date: new Date(year, month + 1, i),
        isCurrentMonth: false,
      });
    }

    return days;
  }, [currentDate]);

  const getTasksForDate = (date) => {
    if (!date) return [];
    return tasks.filter((task) => {
      const taskDate = new Date(task.date);
      return taskDate.toDateString() === date.toDateString();
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return {
          backgroundColor: "#fff3cd",
          color: "#856404",
          border: "1px solid #ffeaa7",
        };
      case "in-progress":
        return {
          backgroundColor: "#cce5ff",
          color: "#004085",
          border: "1px solid #99d3ff",
        };
      case "completed":
        return {
          backgroundColor: "#d4edda",
          color: "#155724",
          border: "1px solid #c3e6cb",
        };
      default:
        return {
          backgroundColor: "#f8f9fa",
          color: "#495057",
          border: "1px solid #e9ecef",
        };
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "#f44336";
      case "medium":
        return "#ff9800";
      case "low":
        return "#2196f3";
      default:
        return "#9e9e9e";
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const taskData = {
      ...newTask,
      _id: selectedTask ? selectedTask._id : Date.now().toString(),
      date: new Date(newTask.date),
      attendees: newTask.attendees
        ? newTask.attendees
            .split(",")
            .map((a) => a.trim())
            .filter((a) => a)
        : [],
    };

    if (selectedTask) {
      setTasks(
        tasks.map((task) => (task._id === selectedTask._id ? taskData : task))
      );
    } else {
      setTasks([...tasks, taskData]);
    }

    setOpenDialog(false);
    setSelectedTask(null);
    setNewTask({
      title: "",
      date: new Date().toISOString().split("T")[0],
      time: "09:00",
      status: "pending",
      priority: "medium",
      client: "",
      location: "",
      isVirtual: false,
      attendees: "",
      notes: "",
    });
  };

  const handleDeleteTask = (id) => {
    setTasks(tasks.filter((task) => task._id !== id));
    setOpenDialog(false);
  };

  const isToday = (date) => {
    return date.toDateString() === today.toDateString();
  };

  const formatDate = (date) => {
    return date.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  };

  const TaskCard = ({ task }) => (
    <div
      style={{
        backgroundColor: "#ffffff",
        borderRadius: "8px",
        borderLeft: `4px solid ${getPriorityColor(task.priority)}`,
        padding: "12px",
        marginBottom: "8px",
        cursor: "pointer",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        transition: "all 0.2s ease",
        ":hover": {
          boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
          transform: "translateY(-2px)",
        },
      }}
      onClick={() => {
        setSelectedTask(task);
        setNewTask({
          ...task,
          date: new Date(task.date).toISOString().split("T")[0],
          attendees: task.attendees?.join(", ") || "",
        });
        setOpenDialog(true);
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.15)";
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "4px",
        }}
      >
        <h4
          style={{
            fontWeight: "600",
            color: "#212121",
            fontSize: "14px",
            margin: 0,
          }}
        >
          {task.title}
        </h4>
        <span
          style={{
            ...getStatusColor(task.status),
            padding: "4px 8px",
            borderRadius: "4px",
            fontSize: "12px",
            fontWeight: "500",
          }}
        >
          {task.status}
        </span>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          fontSize: "12px",
          color: "#757575",
          marginBottom: "4px",
        }}
      >
        <Clock size={12} />
        <span>{task.time}</span>
        {task.isVirtual && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              backgroundColor: "#e3f2fd",
              color: "#1976d2",
              padding: "2px 8px",
              borderRadius: "4px",
            }}
          >
            <Video size={10} />
            <span>Virtual</span>
          </div>
        )}
      </div>
      {task.location && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            fontSize: "12px",
            color: "#757575",
            marginBottom: "4px",
          }}
        >
          <MapPin size={12} />
          <span>{task.location}</span>
        </div>
      )}
      {task.attendees?.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            fontSize: "12px",
            color: "#757575",
          }}
        >
          <Users size={12} />
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {task.attendees.join(", ")}
          </span>
        </div>
      )}
    </div>
  );

  const styles = {
    container: {
      minHeight: "100vh",
      backgroundColor: "#f5f5f5",
      padding: "24px",
    },
    maxWidth: {
      maxWidth: "1200px",
      margin: "0 auto",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "32px",
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: "16px",
    },
    navButton: {
      padding: "8px",
      borderRadius: "4px",
      border: "none",
      backgroundColor: "transparent",
      cursor: "pointer",
      transition: "background-color 0.2s",
      ":hover": {
        backgroundColor: "#e0e0e0",
      },
    },
    title: {
      fontSize: "2rem",
      fontWeight: "bold",
      color: "#212121",
      margin: 0,
    },
    addButton: {
      backgroundColor: "#1976d2",
      color: "white",
      padding: "8px 16px",
      borderRadius: "4px",
      border: "none",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      cursor: "pointer",
      transition: "background-color 0.2s",
      ":hover": {
        backgroundColor: "#1565c0",
      },
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "1fr 300px",
      gap: "24px",
    },
    calendarContainer: {
      backgroundColor: "white",
      borderRadius: "8px",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      padding: "24px",
    },
    calendarHeader: {
      display: "grid",
      gridTemplateColumns: "repeat(7, 1fr)",
      gap: "8px",
      marginBottom: "16px",
    },
    dayHeader: {
      textAlign: "center",
      padding: "8px",
      fontWeight: "600",
      color: "#424242",
    },
    calendarGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(7, 1fr)",
      gap: "8px",
    },
    dayCell: {
      minHeight: "100px",
      padding: "8px",
      border: "1px solid #e0e0e0",
      borderRadius: "4px",
      cursor: "pointer",
      transition: "all 0.2s",
      backgroundColor: "white",
    },
    dayCellInactive: {
      backgroundColor: "#f5f5f5",
      borderColor: "#f0f0f0",
    },
    dayCellToday: {
      border: "2px solid #1976d2",
      backgroundColor: "#e3f2fd",
    },
    dayNumber: {
      fontSize: "14px",
      fontWeight: "500",
      marginBottom: "4px",
    },
    taskChip: {
      fontSize: "12px",
      padding: "4px 8px",
      borderRadius: "4px",
      color: "white",
      fontWeight: "500",
      marginBottom: "2px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      cursor: "pointer",
    },
    sidebar: {
      display: "flex",
      flexDirection: "column",
      gap: "24px",
    },
    sidebarCard: {
      backgroundColor: "white",
      borderRadius: "8px",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      padding: "24px",
    },
    sidebarTitle: {
      fontSize: "1.2rem",
      fontWeight: "600",
      color: "#212121",
      marginBottom: "16px",
    },
    modal: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0,0,0,0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "16px",
      zIndex: 1000,
    },
    modalContent: {
      backgroundColor: "white",
      borderRadius: "8px",
      boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
      width: "100%",
      maxWidth: "500px",
      maxHeight: "90vh",
      overflowY: "auto",
    },
    modalHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "24px",
      borderBottom: "1px solid #e0e0e0",
    },
    modalTitle: {
      fontSize: "1.25rem",
      fontWeight: "600",
      color: "#212121",
      margin: 0,
    },
    closeButton: {
      background: "none",
      border: "none",
      cursor: "pointer",
      color: "#757575",
      padding: "4px",
    },
    modalBody: {
      padding: "24px",
    },
    formGroup: {
      marginBottom: "16px",
    },
    label: {
      display: "block",
      fontSize: "14px",
      fontWeight: "500",
      color: "#424242",
      marginBottom: "4px",
    },
    input: {
      width: "100%",
      padding: "12px",
      border: "1px solid #bdbdbd",
      borderRadius: "4px",
      fontSize: "16px",
      transition: "border-color 0.2s",
      ":focus": {
        outline: "none",
        borderColor: "#1976d2",
        boxShadow: "0 0 0 2px rgba(25,118,210,0.2)",
      },
    },
    select: {
      width: "100%",
      padding: "12px",
      border: "1px solid #bdbdbd",
      borderRadius: "4px",
      fontSize: "16px",
      backgroundColor: "white",
      cursor: "pointer",
    },
    textarea: {
      width: "100%",
      padding: "12px",
      border: "1px solid #bdbdbd",
      borderRadius: "4px",
      fontSize: "16px",
      resize: "vertical",
      minHeight: "80px",
    },
    checkbox: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    checkboxInput: {
      width: "16px",
      height: "16px",
      accentColor: "#1976d2",
    },
    modalFooter: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "24px",
      borderTop: "1px solid #e0e0e0",
      backgroundColor: "#f9f9f9",
    },
    deleteButton: {
      color: "#d32f2f",
      background: "none",
      border: "none",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "4px",
      fontSize: "14px",
      padding: "8px",
    },
    buttonGroup: {
      display: "flex",
      gap: "8px",
    },
    cancelButton: {
      padding: "8px 16px",
      color: "#424242",
      backgroundColor: "#e0e0e0",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      transition: "background-color 0.2s",
    },
    submitButton: {
      padding: "8px 16px",
      backgroundColor: "#1976d2",
      color: "white",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "4px",
      transition: "background-color 0.2s",
    },
    gridCols2: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "16px",
    },
  };

  return (
    <Layout>
      <div style={styles.container}>
        <div style={styles.maxWidth}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.headerLeft}>
              <button
                style={styles.navButton}
                onClick={handlePreviousMonth}
                onMouseEnter={(e) =>
                  (e.target.style.backgroundColor = "#e0e0e0")
                }
                onMouseLeave={(e) =>
                  (e.target.style.backgroundColor = "transparent")
                }
              >
                <ChevronLeft size={20} style={{ color: "#757575" }} />
              </button>
              <h1 style={styles.title}>{formatDate(currentDate)}</h1>
              <button
                style={styles.navButton}
                onClick={handleNextMonth}
                onMouseEnter={(e) =>
                  (e.target.style.backgroundColor = "#e0e0e0")
                }
                onMouseLeave={(e) =>
                  (e.target.style.backgroundColor = "transparent")
                }
              >
                <ChevronRight size={20} style={{ color: "#757575" }} />
              </button>
            </div>
            <button
              style={styles.addButton}
              onClick={() => {
                setSelectedTask(null);
                setNewTask({
                  title: "",
                  date: new Date().toISOString().split("T")[0],
                  time: "09:00",
                  status: "pending",
                  priority: "medium",
                  client: "",
                  location: "",
                  isVirtual: false,
                  attendees: "",
                  notes: "",
                });
                setOpenDialog(true);
              }}
              onMouseEnter={(e) => (e.target.style.backgroundColor = "#1565c0")}
              onMouseLeave={(e) => (e.target.style.backgroundColor = "#1976d2")}
            >
              <Plus size={16} />
              Add Task
            </button>
          </div>

          <div style={styles.grid}>
            {/* Calendar */}
            <div style={styles.calendarContainer}>
              {/* Calendar Header */}
              <div style={styles.calendarHeader}>
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                  (day) => (
                    <div key={day} style={styles.dayHeader}>
                      <span>{day}</span>
                    </div>
                  )
                )}
              </div>

              {/* Calendar Grid */}
              <div style={styles.calendarGrid}>
                {calendarDays.map((day, index) => {
                  const dayTasks = getTasksForDate(day.date);
                  const todayClass = isToday(day.date);

                  let cellStyle = { ...styles.dayCell };
                  if (!day.isCurrentMonth) {
                    cellStyle = { ...cellStyle, ...styles.dayCellInactive };
                  }
                  if (todayClass) {
                    cellStyle = { ...cellStyle, ...styles.dayCellToday };
                  }

                  return (
                    <div
                      key={index}
                      style={cellStyle}
                      onClick={() => {
                        setSelectedTask(null);
                        setNewTask({
                          ...newTask,
                          date: day.date.toISOString().split("T")[0],
                        });
                        setOpenDialog(true);
                      }}
                      onMouseEnter={(e) => {
                        if (!todayClass) {
                          e.target.style.backgroundColor = "#f5f5f5";
                        }
                        e.target.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)";
                      }}
                      onMouseLeave={(e) => {
                        if (!todayClass) {
                          e.target.style.backgroundColor = day.isCurrentMonth
                            ? "white"
                            : "#f5f5f5";
                        }
                        e.target.style.boxShadow = "none";
                      }}
                    >
                      <div
                        style={{
                          ...styles.dayNumber,
                          color: !day.isCurrentMonth
                            ? "#bdbdbd"
                            : todayClass
                            ? "#1976d2"
                            : "#212121",
                        }}
                      >
                        {day.day}
                      </div>

                      <div>
                        {dayTasks.slice(0, 2).map((task, i) => (
                          <div
                            key={i}
                            style={{
                              ...styles.taskChip,
                              backgroundColor: getPriorityColor(task.priority),
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTask(task);
                              setNewTask({
                                ...task,
                                date: new Date(task.date)
                                  .toISOString()
                                  .split("T")[0],
                                attendees: task.attendees?.join(", ") || "",
                              });
                              setOpenDialog(true);
                            }}
                          >
                            {task.title}
                          </div>
                        ))}

                        {dayTasks.length > 2 && (
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#757575",
                              textAlign: "center",
                            }}
                          >
                            +{dayTasks.length - 2} more
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Sidebar */}
            <div style={styles.sidebar}>
              <div style={styles.sidebarCard}>
                <h2 style={styles.sidebarTitle}>Upcoming Tasks</h2>
                <div>
                  {tasks
                    .filter((task) => new Date(task.date) >= today)
                    .sort(
                      (a, b) =>
                        new Date(a.date).getTime() - new Date(b.date).getTime()
                    )
                    .slice(0, 5)
                    .map((task) => (
                      <TaskCard key={task._id} task={task} />
                    ))}
                  {tasks.filter((task) => new Date(task.date) >= today)
                    .length === 0 && (
                    <p style={{ color: "#757575", fontSize: "14px" }}>
                      No upcoming tasks.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Task Dialog */}
          {openDialog && (
            <div style={styles.modal}>
              <div style={styles.modalContent}>
                <form onSubmit={handleSubmit}>
                  {/* Dialog Header */}
                  <div style={styles.modalHeader}>
                    <h2 style={styles.modalTitle}>
                      {selectedTask ? "Edit Task" : "Add New Task"}
                    </h2>
                    <button
                      type="button"
                      style={styles.closeButton}
                      onClick={() => setOpenDialog(false)}
                    >
                      <X size={20} />
                    </button>
                  </div>

                  {/* Dialog Content */}
                  <div style={styles.modalBody}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Title *</label>
                      <input
                        type="text"
                        value={newTask.title}
                        onChange={(e) =>
                          setNewTask({ ...newTask, title: e.target.value })
                        }
                        style={styles.input}
                        required
                      />
                    </div>

                    <div style={styles.gridCols2}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Date *</label>
                        <input
                          type="date"
                          value={newTask.date}
                          onChange={(e) =>
                            setNewTask({ ...newTask, date: e.target.value })
                          }
                          style={styles.input}
                          required
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Time *</label>
                        <input
                          type="time"
                          value={newTask.time}
                          onChange={(e) =>
                            setNewTask({ ...newTask, time: e.target.value })
                          }
                          style={styles.input}
                          required
                        />
                      </div>
                    </div>

                    <div style={styles.gridCols2}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Status</label>
                        <select
                          value={newTask.status}
                          onChange={(e) =>
                            setNewTask({ ...newTask, status: e.target.value })
                          }
                          style={styles.select}
                        >
                          <option value="pending">Pending</option>
                          <option value="in-progress">In Progress</option>
                          <option value="completed">Completed</option>
                        </select>
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Priority</label>
                        <select
                          value={newTask.priority}
                          onChange={(e) =>
                            setNewTask({ ...newTask, priority: e.target.value })
                          }
                          style={styles.select}
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </div>
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Client</label>
                      <input
                        type="text"
                        value={newTask.client}
                        onChange={(e) =>
                          setNewTask({ ...newTask, client: e.target.value })
                        }
                        style={styles.input}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Location</label>
                      <input
                        type="text"
                        value={newTask.location}
                        onChange={(e) =>
                          setNewTask({ ...newTask, location: e.target.value })
                        }
                        style={styles.input}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>
                        Attendees (comma-separated)
                      </label>
                      <input
                        type="text"
                        value={newTask.attendees}
                        onChange={(e) =>
                          setNewTask({ ...newTask, attendees: e.target.value })
                        }
                        style={styles.input}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <div style={styles.checkbox}>
                        <input
                          type="checkbox"
                          id="isVirtual"
                          checked={newTask.isVirtual}
                          onChange={(e) =>
                            setNewTask({
                              ...newTask,
                              isVirtual: e.target.checked,
                            })
                          }
                          style={styles.checkboxInput}
                        />
                        <label
                          htmlFor="isVirtual"
                          style={{ color: "#424242", fontSize: "14px" }}
                        >
                          Virtual Task/Meeting
                        </label>
                      </div>
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Notes</label>
                      <textarea
                        value={newTask.notes}
                        onChange={(e) =>
                          setNewTask({ ...newTask, notes: e.target.value })
                        }
                        style={styles.textarea}
                      />
                    </div>
                  </div>

                  {/* Dialog Actions */}
                  <div style={styles.modalFooter}>
                    <div>
                      {selectedTask && (
                        <button
                          type="button"
                          onClick={() => handleDeleteTask(selectedTask._id)}
                          style={styles.deleteButton}
                          onMouseEnter={(e) =>
                            (e.target.style.color = "#b71c1c")
                          }
                          onMouseLeave={(e) =>
                            (e.target.style.color = "#d32f2f")
                          }
                        >
                          <Trash2 size={16} />
                          Delete
                        </button>
                      )}
                    </div>
                    <div style={styles.buttonGroup}>
                      <button
                        type="button"
                        onClick={() => setOpenDialog(false)}
                        style={styles.cancelButton}
                        onMouseEnter={(e) =>
                          (e.target.style.backgroundColor = "#bdbdbd")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.backgroundColor = "#e0e0e0")
                        }
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        style={styles.submitButton}
                        onMouseEnter={(e) =>
                          (e.target.style.backgroundColor = "#1565c0")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.backgroundColor = "#1976d2")
                        }
                      >
                        <Save size={16} />
                        {selectedTask ? "Update" : "Add"}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default TaskCalendar;
